// --- Dependencies -------------------------------------------------------------------------------
import { TextField } from "@mui/material";
// --- Our Modules --------------------------------------------------------------------------------
import "../field.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    placeholder?: string;
    type?: "text" | "tel" | "email" | "password";
    dir?: "ltr" | "rtl";
    disabled?: boolean;
    error?: string | null;
    onEnter?: () => void;
    /** A textarea instead of one line, for text whose own line breaks are the formatting. */
    multiline?: boolean;
    rows?: number;
    /** The bar's variant — shorter and fixed-width, to sit level with the buttons beside it. */
    compact?: boolean;
};

/**
 * One line of text, or several. MUI's outlined field, wearing our tokens — see `field.css`.
 *
 * **The label does the placeholder's job.** Empty, it sits inside the box; typed into, it shrinks up
 * into the border and stays there. A caption above the field cost a line of height on every form
 * and left the box anonymous the moment it was filled in, which is exactly when somebody scanning
 * back over a long form needs to know what they are looking at.
 *
 * So `placeholder` is only passed through when there is **no** label. With both, MUI draws them on
 * top of each other in the empty state and the field reads as two overlapping words.
 */
export default function TextInput({
    value,
    onChange,
    label,
    placeholder,
    type = "text",
    dir,
    disabled,
    error,
    onEnter,
    multiline,
    rows = 3,
    compact,
}: Props) {
    return (
        <TextField
            className={`field${multiline ? " field--area" : ""}${compact ? " field--compact" : ""}`}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            label={label}
            placeholder={label ? undefined : placeholder}
            type={type}
            disabled={disabled}
            error={!!error}
            helperText={error || undefined}
            multiline={multiline}
            minRows={multiline ? rows : undefined}
            onKeyDown={(event) => {
                if (event.key === "Enter" && !multiline) onEnter?.();
            }}
            slotProps={{ htmlInput: { dir } }}
            variant="outlined"
            fullWidth={!compact}
        />
    );
}
